import { BlurEvent, DataSource, DataSourceAction, DataSourceExecutionEvent, DropdownSelectionEvent, LookupModelSearchEvent, PrintableEvent, Snackbar, Switch, Table, TableCell, TableRow, TableRowCreationEvent, TableRowDisplayEvent, TableRowModeChangeEvent, Textbox, ValidationResult } from "@mcleod/components";
import { ArrayUtil, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutAgenciesWithPrimary } from "./autogen/AutogenLayoutAgenciesWithPrimary";

export class AgenciesWithPrimary extends AutogenLayoutAgenciesWithPrimary {
    private parentMainDataSource: DataSource;
    private agencyField: string;
    public displaySnackbars: boolean = false;
    public commissionsEnabled: boolean = false;

    override onLoad() {

        this.agencyLinkTable.addRowDisplayListener((event: TableRowDisplayEvent) => {
            const agencyTable = this.agencyLinkTable as Table;
            const dataSource = agencyTable.dataSource as DataSource;
            const layoutName: string = dataSource?.layoutName;
            const column = agencyTable.getColumnByCaption("Primary");
            if (column) {
                if (layoutName && layoutName === "lme/dispatch/CallinScriptUsers") {
                    agencyTable.removeColumn(column);
                }
            }
            const row: TableRow = event.getTableRow() as TableRow;
            const modelRow = row.data as ModelRow;
            const isCommissions = StringUtil.equalsIgnoreCase(modelRow.get("is_commissions_resp_code", null), "Y");
            row.cells.forEach((cell: TableCell) => {
                for (const component of cell.components) {
                    if (component instanceof Textbox && component.id === "textboxSplitPercent") {
                        component.setProps({ visible: isCommissions });
                    }
                }
            });
            if (layoutName === "lme/general/user/LMEUserAgencyUsers" && this.commissionsEnabled &&
                !modelRow.isNull("pay_commissions_when")) {
                this.configureTableRowUsingPayWhen(modelRow.get("pay_commissions_when"), row);
            }
        });

        this.agencyLinkTable.onValidate = (table: Table): ValidationResult[] => {
            return this.validateTableRecords();
        }
    }

    public setup(parentAgencyLinkDataSource: DataSource, parentMainDataSource: DataSource, agencyField: string) {
        this.useProvidedAgencyLinkDataSource(parentAgencyLinkDataSource);
        this.parentMainDataSource = parentMainDataSource;
        this.agencyField = agencyField;
        if (agencyField == null)
            this.agencyLinkTable.removeColumn("Primary");
        else {
            this.parentMainDataSource.addBeforeExecutionListener((event: DataSourceExecutionEvent) => this.setPrimaryAgencyOnParent());
            this.setPrimaryTogglesFromParent();
            this.sourceAgencyLink.addAfterExecutionListener((event: DataSourceExecutionEvent) => this.setPrimaryTogglesFromParent(event));
        }
    }

    public useProvidedAgencyLinkDataSource(sourceAgencyLink: DataSource) {
        this.sourceAgencyLink = sourceAgencyLink;
        this.replaceMainDatasource(this.sourceAgencyLink);
        this.displayData(null, this.mainDataSource.data, null);
    }

    private setPrimaryAgencyOnParent() {
        const primaryAgencyID = this.getPrimaryAgencyId();
        if (primaryAgencyID != null) {
            this.parentMainDataSource.activeRow.set("primary_agency", primaryAgencyID);
        }
    }

    public setPrimaryTogglesFromParent(event?: DataSourceExecutionEvent) {
        this.sourceAgencyLink.preventChangeNotifications = true;
        try {
            if (event == null || event.getAction() === DataSourceAction.SEARCH) {
                for (const row of this.sourceAgencyLink.data) {
                    const primaryAgencyOnParent = this.parentMainDataSource?.activeRow?.get(this.agencyField);
                    const isPrimary: boolean = row.get("agency_id") === primaryAgencyOnParent;
                    row.set("is_primary", isPrimary === true ? "Y" : "N");
                    // Reset "is_primary"'s data so that the datasource isn't marked as changed,
                    // which would enable the save button as soon as the slideout opens
                    row.resetOriginalData("is_primary");
                }
            }
        }
        finally {
            this.sourceAgencyLink.preventChangeNotifications = false;
        }
    }

    private validateTableRecords(): ValidationResult[] {
        const dataSource = this.agencyLinkTable.dataSource as DataSource;
        const layoutName = dataSource?.layoutName;
        if (layoutName && layoutName === "lme/dispatch/CallinScriptUsers") {
            return [this.validateDuplicates()];
        } else {
            return [this.validatePrimary(), this.validateDuplicates()];
        }
    }

    private validatePrimary(): ValidationResult {
        if (this.agencyField !== null) {
            const primaryCount = this.countPrimaryAgency();
            if (primaryCount > 1) {
                const message = "More than one Responsibility User record is set as primary. Please select only one user to be the primary user.";
                if (this.displaySnackbars === true) {
                    this.showWarningSnackbar(message, "invalidPrimaryRF");
                }
                return { isValid: false, validationMessage: message, component: this.agencyLinkTable };
            }
            else if (primaryCount === 0) {
                const message = "This record currently does not have a primary Responsibility User assigned.  Please choose one Responsibility User to be the primary user.";
                if (this.displaySnackbars === true) {
                    this.showWarningSnackbar(message, "invalidPrimaryRF");
                }
                return { isValid: false, validationMessage: message, component: this.agencyLinkTable };
            }
        }
        return { isValid: true, component: this.agencyLinkTable };
    }

    private countPrimaryAgency() {
        let primaryCount = 0;
        for (const row of this.agencyLinkTable.data) {
            if (row.get("is_primary") === "Y") {
                primaryCount++;
            }
        }
        return primaryCount;
    }

    private validateDuplicates(): ValidationResult {
        const countPerCode: Map<string, number> = new Map();
        for (const row of this.agencyLinkTable.data) {
            const agencyId = row.get("agency_id");
            const count = countPerCode.get(agencyId);
            countPerCode.set(agencyId, count == null ? 1 : count + 1);
        }
        const duplicates: string[] = [];
        for (const entry of countPerCode.entries()) {
            if (entry[1] > 1) {
                duplicates.push(entry[0]);
            }
        }

        if (ArrayUtil.isEmptyArray(duplicates)) {
            return { isValid: true, component: this.agencyLinkTable };
        }

        const message = "The following have been entered more than once: " + duplicates.join(", ") + ".  Please remove duplicate entries.";
        if (this.displaySnackbars === true) {
            this.showWarningSnackbar(message, null);
        }
        return { isValid: false, validationMessage: message, component: this.agencyLinkTable };
    }

    private showWarningSnackbar(message: string, id: string) {
        Snackbar.showWarningSnackbar(message, { persist: true, id: id })
    }

    public getPrimaryAgencyId(): string {
        for (const row of this.agencyLinkTable.data) {
            if (row.get("is_primary") === "Y") {
                return row.get("agency_id");
            }
        }
        return null;
    }

    public getPrimaryAgencyField(): string {
        if (this.parentMainDataSource["extraFields"]) {
            for (const field of this.parentMainDataSource["extraFields"]) {
                if (field.includes("primary_agency")) {
                    return field;
                }
            }
        }
        return null;
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxAgencyId.  */
    textboxAgencyIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y";
    }

    switchCommissionsOnClick(event) {
        const switchCommissions = event.target as Switch;
        const tb = event.target.row.findComponentById("textboxSplitPercent");

        if (tb) {
            tb.visible = switchCommissions.checked;
            if (!switchCommissions.checked) {
                tb.text = "";
            }
        }
    }

    textboxSplitPercentOnPrintable(event: PrintableEvent) {
        const textboxSplitPercent = event.target as Textbox;
        textboxSplitPercent.fillRow = true;
    }

    textboxPayCommissionsWhenAfterDropdownSelection(event: DropdownSelectionEvent) {
        const dropdown = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(dropdown) as TableRow;
        const modelRow = tableRow.data as ModelRow;
        if (event.hasSelection()) {
            this.configureTableRowUsingPayWhen(event.selection.value as string, tableRow);
        }
    }

    configureTableRowUsingPayWhen(payWhenValue: string, tableRow: TableRow) {
        if (!payWhenValue)
            return;
        const modelRow = tableRow.data as ModelRow;
        const textboxEntryRole = tableRow.findComponentById("textboxCommOrderEntryRole") as Textbox;
        const textboxDispatchRole = tableRow.findComponentById("textboxCommDispatchRole") as Textbox;
        const textboxEffectiveDate = tableRow.findComponentById("textboxEffectiveDate") as Textbox;
        switch (payWhenValue) {
            case "A":
                textboxEntryRole.enabled = true;
                textboxEntryRole.required = true;
                textboxDispatchRole.enabled = true;
                textboxDispatchRole.required = true;
                textboxEffectiveDate.enabled = true;
                textboxEffectiveDate.required = true;
                break;
            case "O":
                textboxEntryRole.enabled = true;
                textboxEntryRole.required = true;
                textboxDispatchRole.enabled = false;
                textboxDispatchRole.required = false;
                modelRow.set("comm_dispatcher_role", null);
                textboxEffectiveDate.enabled = true;
                textboxEffectiveDate.required = true;
                break;
            case "D":
                textboxEntryRole.enabled = false;
                textboxEntryRole.required = false;
                modelRow.set("comm_order_entry_role", null);
                textboxDispatchRole.enabled = true;
                textboxDispatchRole.required = true;
                textboxEffectiveDate.enabled = true;
                textboxEffectiveDate.required = true;
                break;
            case "N":
                textboxEntryRole.enabled = false;
                textboxEntryRole.required = false;
                modelRow.set("comm_order_entry_role", null);
                textboxDispatchRole.enabled = false;
                textboxDispatchRole.required = false;
                modelRow.set("comm_dispatcher_role", null);
                textboxEffectiveDate.enabled = false;
                textboxEffectiveDate.required = false;
                modelRow.set("effective_date", null);
                break;
            default:
                break;
        }
        tableRow.displayComponentValues();
    }

    textboxAgencyIdOnBlur(event: BlurEvent) {
        // CHECK IF THIS IS A DUPLICATE AGENCY
        const textboxAgencyId = event.target as Textbox;
        const agencyId = textboxAgencyId.getDataValue() as string;
        for (const tableRow of this.agencyLinkTable.rows) {
            const rowAgencyId = tableRow.data.get("agency_id");
            if (rowAgencyId === agencyId) {
                Snackbar.showWarningSnackbar("This agency "+agencyId+ " has already been added to the list. Duplicate agencies are not allowed.");
                textboxAgencyId.text = null;
                textboxAgencyId.getRelevantModelRow().set("agency_id", null);
                textboxAgencyId.focus();
                break;
            }
        }
    }
}
